import { useEffect, useState } from 'react';
import axios from 'axios';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { Page, Layout, Card, TextField, Button, BlockStack, Text } from '@shopify/polaris';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const emailSchema = z
    .string()
    .email('Please enter a valid email address.')
    .or(z.literal('').transform(() => null));

function ApplicationSettings() {
    const { addToast } = useToast();
    const BASE_URL = import.meta.env.VITE_API_URL;
    const queryClient = useQueryClient();
    const [notificationEmail, setNotificationEmail] = useState('');
    const [inventoryAdjustmentEmail, setInventoryAdjustmentEmail] = useState('');
    const [inventoryAdjustmentThreshold, setInventoryAdjustmentThreshold] = useState('');
    const [fieldError, setFieldError] = useState(null);
    const [inventoryAdjustmentEmailError, setInventoryAdjustmentEmailError] = useState(null);
    const [inventoryAdjustmentThresholdError, setInventoryAdjustmentThresholdError] = useState(null);

    const settingsQueryKey = ['application-settings'];

    const { data, isLoading, isError } = useQuery({
        queryKey: settingsQueryKey,
        queryFn: async () => {
            const res = await axios.get(`${BASE_URL}/api/app/application-settings`);
            return res.data?.data || {};
        },
    });

    useEffect(() => {
        if (!data) return;
        setNotificationEmail(data.notificationEmail || '');
        setInventoryAdjustmentEmail(data.inventoryAdjustmentEmail || '');
        setInventoryAdjustmentThreshold(data.inventoryAdjustmentThreshold || '');
    }, [data]);

    useEffect(() => {
        if (!isError) return;
        addToast({ type: 'error', message: 'Failed to load settings' });
    }, [isError, addToast]);

    const saveSettingsMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await axios.post(`${BASE_URL}/api/app/application-settings`, payload);
            return response.data;
        },
        onSuccess: () => {
            addToast({ type: 'success', message: 'Settings saved successfully' });
            queryClient.invalidateQueries({ queryKey: settingsQueryKey });
        },
        onError: (err) => {
            console.error('Validation or saving error:', err);

            if (err instanceof z.ZodError) {
                addToast({ type: 'error', message: err.errors[0]?.message || 'Invalid input' });
            } else {
                const errorMessage = err?.response?.data?.errors?.[0]?.message || 'Error saving settings';
                addToast({ type: 'error', message: errorMessage });
            }
        },
    });

    const handleSave = async () => {
        // clear existing field errors
        setFieldError(null);
        setInventoryAdjustmentEmailError(null);
        setInventoryAdjustmentThresholdError(null);
        try {
            const validation = emailSchema.safeParse(notificationEmail);

            if (!validation.success) {
                setFieldError('Enter a valid email address');
                return;
            }
            const parsedEmail = emailSchema.parse(notificationEmail.trim());

            // Validate inventory adjustment email
            const inventoryEmailValidation = emailSchema.safeParse(inventoryAdjustmentEmail);
            if (!inventoryEmailValidation.success) {
                setInventoryAdjustmentEmailError('Enter a valid email address');
                return;
            }
            const parsedInventoryEmail = emailSchema.parse(inventoryAdjustmentEmail.trim());

            // Validate threshold (optional, but if provided must be a positive number)
            let parsedThreshold = null;
            if (inventoryAdjustmentThreshold !== '') {
                const thresholdNum = Number(inventoryAdjustmentThreshold);
                if (isNaN(thresholdNum) || thresholdNum < 0) {
                    setInventoryAdjustmentThresholdError('Enter a valid positive number');
                    return;
                }
                parsedThreshold = thresholdNum;
            }

            saveSettingsMutation.mutate({
                notificationEmail: parsedEmail,
                inventoryAdjustmentEmail: parsedInventoryEmail,
                inventoryAdjustmentThreshold: parsedThreshold,
            });
        } catch (err) {
            console.error('Validation or saving error:', err);

            if (err instanceof z.ZodError) {
                addToast({ type: 'error', message: err.errors[0]?.message || 'Invalid input' });
            } else {
                const errorMessage = err?.response?.data?.errors?.[0]?.message || 'Error saving settings';
                addToast({ type: 'error', message: errorMessage });
            }
        }
    };

    if (isLoading) {
        return <div>Loading settings...</div>;
    }

    return (
        <Page fullWidth title="Application Settings">
            <Layout>
                <Layout.Section>
                    {isError && (
                        <div style={{ marginBottom: '1rem' }}>
                            <Text variant="bodyLg" tone="critical">
                                Failed to load settings. Please try again later.
                            </Text>
                        </div>
                    )}

                    <Card sectioned>
                        <Text variant="headingMd">Voucher Programs</Text>
                        <br />
                        <TextField
                            label="Creation Notification Email"
                            value={notificationEmail}
                            onChange={(value) => {
                                setNotificationEmail(value);
                                if (fieldError) setFieldError(null); // Clear error on input
                            }}
                            type="email"
                            autoComplete="email"
                            error={fieldError}
                        />
                    </Card>
                    <div style={{ marginTop: '1rem' }} />
                    <Card sectioned>
                        <Text variant="headingMd">Inventory Adjustments</Text>
                        <br />
                        <BlockStack gap="400">
                            <TextField
                                label="Notification Email"
                                value={inventoryAdjustmentEmail}
                                onChange={(value) => {
                                    setInventoryAdjustmentEmail(value);
                                    if (inventoryAdjustmentEmailError) setInventoryAdjustmentEmailError(null);
                                }}
                                type="email"
                                autoComplete="email"
                                error={inventoryAdjustmentEmailError}
                            />
                            <TextField
                                label="Threshold"
                                value={inventoryAdjustmentThreshold}
                                onChange={(value) => {
                                    setInventoryAdjustmentThreshold(value);
                                    if (inventoryAdjustmentThresholdError) setInventoryAdjustmentThresholdError(null);
                                }}
                                type="number"
                                min="0"
                                error={inventoryAdjustmentThresholdError}
                            />
                        </BlockStack>
                    </Card>
                    <div style={{ marginTop: '1rem' }}>
                        <Button onClick={handleSave} loading={saveSettingsMutation.isPending}>
                            Save Settings
                        </Button>
                    </div>
                </Layout.Section>
            </Layout>
        </Page>
    );
}

export default ApplicationSettings;
